import {Flex, Input, Modal, Space, Typography} from "antd";
import React, {useEffect} from "react";
import '@mdxeditor/editor/style.css';
import {
    MDXEditor
} from '@mdxeditor/editor';
import {TagFlow} from "../../components/TagFlow";
import {Tag} from "../../feature/project/fields";
import {useAppDispatch, useAppSelector} from "../../feature/tools";
import {useParams} from "react-router-dom";
import {Status} from "../../feature/project/status";
import {fetchTags} from "../../feature/project/projectsSlice";
import {addChapterTag, removeChapterTag} from "../../feature/project/chapterSlice";
import {Chapter} from "../../feature/project/story";
import {DebounceInput} from "../../components/DebounceInput";

export interface ChapterProps {
    chapter: Chapter,
    visible: boolean,
    onCancel: () => void,
    onSave: (chapter: Chapter) => void,
}

export const AddChapterModal = (props: ChapterProps) => {
    const dispatch = useAppDispatch();
    const {projectId} = useParams();
    const allTags = useAppSelector(state => state.projects.tags)
    const status = useAppSelector(state => state.components.status)
    const [hasChanges, setHasChanges] = React.useState(false)
    const [title, setTitle] = React.useState(props.chapter.title)
    const [description, setDescription] = React.useState(props.chapter.description)
    const [tags, setTags] = React.useState<Array<Tag>>(props.chapter.tags || [])

    useEffect(() => {
        setTitle(props.chapter.title)
        setDescription(props.chapter.description || '')
        setTags(props.chapter.tags || [])
    }, [props.visible, props.chapter, props.chapter.tags])

    useEffect(() => {
        setHasChanges(title !== props.chapter.title || description !== props.chapter.description )
    }, [title, description])

    useEffect(() => {
        if (status == Status.Idle && projectId && props.visible) {
            if (props.chapter.id) {
                dispatch(fetchTags({projectId: parseInt(projectId)}))
            }
        }
    }, [status, dispatch])

    return (
        <Modal
            maskClosable={false}
            open={props.visible}
            destroyOnClose={true}
            title={props.chapter.id ? 'Chapter Settings' : `Add Chapter`}
            okText={'Save'}
            okButtonProps={{disabled: !hasChanges}}
            onCancel={() => props.onCancel()}
            onOk={() => props.onSave({
                id: props.chapter.id,
                title: title || "",
                description: description || "",
                tags: tags
            })
            }
            width={600}
        >
            <Flex gap={16}>
                <Space direction={'vertical'} style={{width: '100%', maxHeight: 400, overflowY: 'auto', scrollbarWidth: "none"}} size={16}>
                    <div>
                        <Typography style={{fontWeight: 700}}>Title</Typography>
                        <DebounceInput value={title} onChange={(text) => setTitle(text)}/>
                    </div>
                    <div>
                        <Typography style={{fontWeight: 700}}>Content</Typography>
                        <MDXEditor
                            className={"markdown-text"}
                            markdown={description || ""}
                            onChange={(e) => setDescription(e)}
                            plugins={[]}
                        />
                    </div>
                    <div>
                        <Typography style={{fontWeight: 700}}>Tags</Typography>
                        <TagFlow allTags={allTags}
                                 tags={tags}
                                 onTagAdded={(tag) => {
                                     setTags([...tags, tag])
                                     if (props.chapter.id && projectId) {
                                         dispatch(addChapterTag({
                                             projectId: parseInt(projectId),
                                             chapterId: props.chapter.id,
                                             tag: {title: tag.title}
                                         }))
                                     }
                                 }}
                                 onTagDeleted={(tag) => {
                                     if (tag.id && props.chapter.id && projectId) {
                                         dispatch(removeChapterTag({
                                             projectId: parseInt(projectId),
                                             chapterId: props.chapter.id,
                                             tagId: tag.id
                                         }))
                                     }
                                 }}
                        />
                    </div>
                </Space>
            </Flex>
        </Modal>
    );
}